import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, ChevronLeft, ChevronRight, Search, X, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { getProductImage } from '../utils/imageHelper';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import ProcessStrip from '@/components/ProcessStrip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getMediaUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API}/files/${path}`;
};

const isVideoPath = (path) => {
  const lower = (path || '').toLowerCase();
  return lower.endsWith('.mp4') || lower.endsWith('.mov');
};

const MediaGallery = ({ mediaPaths, imageUrl, name }) => {
  const paths = mediaPaths && mediaPaths.length > 0 ? mediaPaths : (imageUrl ? [imageUrl] : []);

  // Always start on the first image, not necessarily index 0 (which could be a video)
  const firstImageIdx = paths.findIndex((p) => !isVideoPath(p));
  const [current, setCurrent] = useState(firstImageIdx >= 0 ? firstImageIdx : 0);

  // No media at all, or everything is a video → show placeholder
  if (paths.length === 0 || firstImageIdx < 0) {
    return (
      <div className="w-full h-80 bg-primary/10 flex flex-col items-center justify-center gap-2">
        <span className="text-5xl select-none">🌿</span>
        <span className="text-xs font-medium text-primary/60 uppercase tracking-wide">Cardamom</span>
      </div>
    );
  }

  const src = getMediaUrl(paths[current]);
  const video = isVideoPath(paths[current]);

  return (
    <div className="relative group">
      {video ? (
        <video src={src} controls className="w-full h-80 object-cover bg-black" />
      ) : (
        <img src={src} alt={name} className="w-full h-80 object-cover" />
      )}
      {paths.length > 1 && (
        <>
          <button onClick={(e) => { e.preventDefault(); setCurrent((current - 1 + paths.length) % paths.length); }} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronLeft size={18} />
          </button>
          <button onClick={(e) => { e.preventDefault(); setCurrent((current + 1) % paths.length); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {paths.map((_, i) => (
              <button key={i} onClick={(e) => { e.preventDefault(); setCurrent(i); }} className={`w-2 h-2 rounded-full transition-colors ${i === current ? 'bg-white' : 'bg-white/50'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Products = () => {
  useDocumentHead({ path: '/products' });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGrade, setActiveGrade] = useState('all');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Extract unique grades from products
  const grades = useMemo(() => {
    const uniqueGrades = [...new Set(products.map(p => p.size))];
    return uniqueGrades.sort();
  }, [products]);

  // Filter products
  const filteredProducts = useMemo(() => {
    let result = products;
    if (activeGrade !== 'all') {
      result = result.filter(p => p.size === activeGrade);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.size.toLowerCase().includes(q) ||
        (p.seller_name && p.seller_name.toLowerCase().includes(q))
      );
    }
    return result;
  }, [products, activeGrade, searchQuery]);

  if (loading) {
    return (
      <div data-testid="products-loading" className="pt-32 flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  return (
    <div data-testid="products-page" className="pt-32 pb-20 md:pb-0">

      {/* FIX 1 — Compact 2-line header (max ~70px) */}
      <div className="px-4 pt-3 pb-2" data-testid="products-hero">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-600 leading-none mb-1">Our Range</p>
        <h1 className="font-serif text-2xl font-bold text-foreground leading-tight">Products</h1>
      </div>

      {/* Search & Filter — tight, 8px gaps */}
      <div className="px-4 pb-3 border-b border-border" data-testid="products-filter-section">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col md:flex-row gap-2 items-start md:items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                data-testid="products-search-input"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name, grade, or seller..."
                className="w-full pl-10 pr-9 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  data-testid="products-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Grade Filter Pills */}
            <div className="flex flex-wrap gap-2" data-testid="products-grade-filters">
              <button
                data-testid="grade-filter-all"
                onClick={() => setActiveGrade('all')}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                  activeGrade === 'all'
                    ? 'bg-primary text-white'
                    : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                }`}
              >
                All Grades ({products.length})
              </button>
              {grades.map(grade => (
                <button
                  key={grade}
                  data-testid={`grade-filter-${grade}`}
                  onClick={() => setActiveGrade(grade)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                    activeGrade === grade
                      ? 'bg-primary text-white'
                      : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                >
                  {grade} ({products.filter(p => p.size === grade).length})
                </button>
              ))}
            </div>
          </div>

          {/* Active filter summary */}
          {(activeGrade !== 'all' || searchQuery) && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="products-filter-summary">
              <span>Showing {filteredProducts.length} of {products.length} products</span>
              <button
                onClick={() => { setActiveGrade('all'); setSearchQuery(''); }}
                data-testid="products-clear-all-filters"
                className="text-primary text-xs font-semibold hover:underline ml-1"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <section className="py-6" data-testid="products-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-16" data-testid="products-no-results">
              <Package className="mx-auto text-muted-foreground mb-4" size={48} />
              <p className="text-lg text-muted-foreground mb-2">No products found</p>
              <p className="text-sm text-muted-foreground mb-4">
                {searchQuery ? `No results for "${searchQuery}"` : 'No products match the selected grade.'}
              </p>
              <button
                onClick={() => { setActiveGrade('all'); setSearchQuery(''); }}
                className="text-primary font-semibold text-sm hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6 lg:gap-8">
              {filteredProducts.map((product, index) => {
                const imgSrc = getProductImage(product);
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3) }}
                    viewport={{ once: true }}
                    data-testid={`product-card-${index}`}
                    className="product-card bg-white rounded-xl overflow-hidden border border-primary/10 hover:border-primary/30 transition-colors"
                  >
                    <Link to={`/products/${product.slug || product.id}`} className="block cursor-pointer">
                      {/* ── Image ── */}
                      <div className="relative overflow-hidden">
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={product.name}
                            className="w-full h-[130px] md:h-64 object-cover"
                          />
                        ) : (
                          <div className="w-full h-[130px] md:h-64 bg-primary/10 flex flex-col items-center justify-center gap-1">
                            <span className="text-3xl md:text-5xl select-none">🌿</span>
                            <span className="text-[10px] md:text-xs font-medium text-primary/60 uppercase tracking-wide hidden md:block">Cardamom</span>
                          </div>
                        )}

                        {product.listing_status === 'sold' && (
                          <div className="absolute inset-0 bg-blue-600/80 flex items-center justify-center">
                            <span className="text-white font-serif text-xl md:text-3xl font-bold tracking-widest">SOLD</span>
                          </div>
                        )}
                      </div>

                      {/* ── Card body ── */}
                      <div className="p-2 md:p-6">
                        {/* Grade badge */}
                        <span className="inline-block px-2 py-0.5 bg-primary/10 text-primary text-[10px] md:text-xs font-semibold uppercase tracking-wide rounded-full mb-1.5 md:mb-3">
                          {product.size}
                        </span>

                        {/* Product name */}
                        <h3 className="font-serif text-[13px] md:text-2xl font-semibold text-foreground mb-1 md:mb-2 line-clamp-2 leading-snug">
                          {product.name}
                        </h3>

                        {/* Seller — the firm, not the listing seller; the actual seller stays admin-only */}
                        <p className="text-[11px] md:text-xs text-muted-foreground truncate mb-1 md:mb-3">
                          <span className="hidden md:inline">Sold by </span>
                          <span className="font-medium text-foreground">Spice One Merchants</span>
                        </p>

                        {/* Description — desktop only */}
                        <p className="hidden md:block text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-3">
                          {product.description}
                        </p>

                        {/* CTA */}
                        <div className="mt-1.5 md:mt-0">
                          {product.listing_status === 'sold' ? (
                            <span className="text-blue-600 text-[11px] md:text-sm font-semibold">View Details</span>
                          ) : product.listing_status === 'expired' ? (
                            <span className="text-orange-600 text-[11px] md:text-sm font-semibold">Enquiries Closed</span>
                          ) : (
                            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                              <span className="whitespace-nowrap text-primary text-[11px] md:text-sm font-semibold inline-flex items-center gap-1 hover:underline">
                                View details <ArrowRight size={12} />
                              </span>
                              <span className="ml-auto whitespace-nowrap text-muted-foreground text-[11px] md:text-sm hover:text-primary hover:underline">
                                Enquire
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Process strip — featured gallery photos, managed from Admin → Gallery */}
      <ProcessStrip className="bg-muted" />
    </div>
  );
};

export default Products;
